import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui';
import { PlatformCheckoutBrand } from '../components/auth/PlatformCheckoutBrand';
import { PlatformCheckoutShell } from '../components/auth/PlatformCheckoutShell';
import {
  FinishSignupFeatureComparison,
} from '../components/subscription/FinishSignupFeatureComparison';
import { CheckCircle, Loader2 } from 'lucide-react';
import {
  fetchSubscriptionQuery,
  fetchPlansQuery,
  invalidateSubscriptionQueries,
} from '../queries/subscription';
import { buildSubscriptionStatusFromResponse } from '../utils/subscriptionStatus';
import { AuthService } from '../services/auth';
import {
  Plan,
  type Region,
  resolveSubscriptionTierForDisplay,
  resolveSubscriptionPlanPrice,
  isAdaptivePricingCountry,
  normalizeClubPlanTier,
} from '@court-sync/shared';
import { AdaptivePricingNote } from '../components/subscription/AdaptivePricingNote';
import { toast } from 'react-hot-toast';
import regionService from '../services/regionService';
import { getCurrencyFromRegion, type Currency } from '../utils/currency';
import { academyService } from '../services/academy';
import { subscriptionService } from '../services/subscription';
import {
  trackCheckoutStarted,
  trackFinishSignupView,
  trackPlanSelected,
  trackStarterLimitationsAcknowledged,
} from '../utils/gtmTracking';
import { clearPricingSelection, readPricingSelection } from '../utils/pricingSelection';
import { useResolvedThemeIsDark } from '../store/theme';
import {
  buildFinishSignupCancelUrl,
  clearCheckoutPlanPreselection,
  clearStoredBillingInterval,
  isFinishSignupCheckoutCancelled,
  readStoredBillingInterval,
  resolveInitialBillingInterval,
  SELECTED_BILLING_INTERVAL_STORAGE_KEY,
} from '../utils/billingInterval';
import { readOnboardingDraft, clearOnboardingDraft } from '../utils/onboardingDraft';
import { useAuthStore } from '../store/auth';
import {
  buildPlanRecommendation,
  buildClubPlanRecommendation,
  deriveOperatingProfileFlags,
  formatPlanTierLabel,
  formatClubPlanTierLabel,
  findClubPlanByTier,
  findPlanByTier,
  getStarterLimitationSummary,
  inferOrgOperationTypeForCheckout,
  resolveCheckoutPlanPreselection,
  resolveCheckoutOrganizationId,
  resolveFinishSignupRecommendedClubTier,
  resolveOnboardingCheckoutTiers,
  resolveOnboardingSelectedPlanTier,
  shouldAutoCheckoutFromOnboarding,
  CLUB_TIER_FOR_ACADEMY_BUNDLE,
} from '../utils/planRecommendationUi';
import { CombinedOnboardingPlanSelection } from '../components/onboarding/CombinedOnboardingPlanSelection';
import { ClubOnboardingPlanSelection } from '../components/onboarding/ClubOnboardingPlanSelection';
import { OnboardingPlanSelection } from '../components/onboarding/OnboardingPlanSelection';
import { StarterLimitationsModal } from '../components/onboarding/StarterLimitationsModal';
import type {
  Academy,
  AcademyOperatingProfileInput,
  OrgOperationType,
  SubscriptionTier,
  ClubPlanTier,
  SubscriptionProductLine,
} from '@court-sync/shared';

/** Match landing/register `planId` to API plans (Stripe id, metadata internalId, or tier slug). */
function findPlanByPreselectionParam(
  plans: Plan[],
  planIdParam: string,
  productLine: SubscriptionProductLine = 'academy',
): Plan | undefined {
  const trimmed = planIdParam.trim();
  if (!trimmed) return undefined;
  const byStripeId = plans.find((p) => p.id === trimmed);
  if (byStripeId) return byStripeId;
  const lower = trimmed.toLowerCase();
  const byInternal = plans.find((p) => (p.internalId ?? '').toLowerCase() === lower);
  if (byInternal) return byInternal;

  if (productLine === 'club') {
    if (lower === 'starter' || lower === 'professional') {
      const byClubTier = findClubPlanByTier(plans, lower);
      if (byClubTier) return byClubTier;
    }
    const clubTierFromInternal = normalizeClubPlanTier(lower);
    if (clubTierFromInternal) {
      return findClubPlanByTier(plans, clubTierFromInternal);
    }
  }

  return plans.find(
    (p) => resolveSubscriptionTierForDisplay(p.name, p.internalId ?? null) === lower
  );
}

export default function FinishSignup() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [clubPlans, setClubPlans] = useState<Plan[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subscribingPlanId, setSubscribingPlanId] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>('AUD');
  const returnedFromCheckoutCancel = isFinishSignupCheckoutCancelled(searchParams);
  const billingIntervalFromUrl = searchParams.get('billingInterval');
  const billingIntervalFromStorage =
    typeof window !== 'undefined'
      ? sessionStorage.getItem(SELECTED_BILLING_INTERVAL_STORAGE_KEY)
      : null;
  const [billingInterval] = useState<'month' | 'year'>(() =>
    resolveInitialBillingInterval(billingIntervalFromUrl, billingIntervalFromStorage),
  );
  const [showAdaptivePricing, setShowAdaptivePricing] = useState(false);
  const [academyOperatingProfile, setAcademyOperatingProfile] = useState<AcademyOperatingProfileInput | null>(null);
  const [starterModalOpen, setStarterModalOpen] = useState(false);
  const [featuresModalOpen, setFeaturesModalOpen] = useState(false);
  const [pendingStarterPlan, setPendingStarterPlan] = useState<Plan | null>(null);
  const onboardingDraft = useMemo(() => readOnboardingDraft(), []);
  const authUser = useAuthStore((state) => state.user);
  const isDark = useResolvedThemeIsDark();
  const autoCheckoutStartedRef = useRef(false);

  const userEmail = location.state?.email;
  const isSocialLogin = location.state?.isSocialLogin;
  const orgSetupCompleted =
    location.state?.orgSetupCompleted ?? location.state?.academyCreated;
  const recommendedTierFromState = location.state?.recommendedTier as SubscriptionTier | undefined;
  const selectedPlanTierFromState = location.state?.selectedPlanTier as SubscriptionTier | undefined;
  const recommendedClubTierFromState = location.state?.recommendedClubTier as ClubPlanTier | undefined;
  const selectedClubPlanTierFromState = location.state?.selectedClubPlanTier as ClubPlanTier | undefined;
  const orgOperationType = useMemo(
    () =>
      inferOrgOperationTypeForCheckout(
        onboardingDraft,
        location.state?.orgOperationType as OrgOperationType | undefined,
        authUser,
      ),
    [authUser, location.state?.orgOperationType, onboardingDraft],
  );
  const organizationIdFromState = location.state?.organizationId as string | undefined;
  const [checkoutOrganizationId, setCheckoutOrganizationId] = useState<string | undefined>(() =>
    resolveCheckoutOrganizationId(onboardingDraft, {
      organizationIdFromState,
    })
  );

  const planRecommendation = useMemo(() => {
    const profile = deriveOperatingProfileFlags(
      academyOperatingProfile ?? onboardingDraft.operatingProfile ?? {}
    );
    return buildPlanRecommendation(profile);
  }, [academyOperatingProfile, onboardingDraft.operatingProfile]);

  const clubPlanRecommendation = useMemo(
    () => buildClubPlanRecommendation(onboardingDraft.clubOperatingProfile ?? {}),
    [onboardingDraft.clubOperatingProfile],
  );

  const recommendedTier =
    recommendedTierFromState ??
    planRecommendation.recommendedTier;

  const selectedPlanTier =
    selectedPlanTierFromState ??
    resolveOnboardingSelectedPlanTier(onboardingDraft, planRecommendation);

  const recommendedClubTier =
    recommendedClubTierFromState ??
    clubPlanRecommendation.recommendedTier;

  const selectedClubPlanTier =
    selectedClubPlanTierFromState ??
    resolveFinishSignupRecommendedClubTier(onboardingDraft, onboardingDraft.clubOperatingProfile);

  const [activeSelectedPlanTier, setActiveSelectedPlanTier] =
    useState<SubscriptionTier>(selectedPlanTier);
  const [activeSelectedClubPlanTier, setActiveSelectedClubPlanTier] =
    useState<ClubPlanTier>(selectedClubPlanTier);

  // Get planId from URL query params (from landing page) or sessionStorage (from registration)
  const planIdFromUrl = returnedFromCheckoutCancel ? null : searchParams.get('planId');
  const planIdFromStorage = returnedFromCheckoutCancel
    ? null
    : (sessionStorage.getItem('selectedAcademyPlanId') ??
        sessionStorage.getItem('selectedClubPlanId') ??
        sessionStorage.getItem('selectedPlanId'));
  const checkoutPlanId = returnedFromCheckoutCancel
    ? null
    : resolveCheckoutPlanPreselection(planIdFromUrl || planIdFromStorage, orgOperationType, onboardingDraft);

  useEffect(() => {
    if (!returnedFromCheckoutCancel) return;
    clearCheckoutPlanPreselection();
    navigate('/finish-signup', { replace: true, state: location.state });
  }, [returnedFromCheckoutCancel, navigate, location.state]);

  useEffect(() => {
    if (billingIntervalFromStorage) {
      clearStoredBillingInterval();
    }
    fetchPlans();
    checkUserSetup();
  }, []);

  // GTM: Track finish-signup page view for funnel analysis
  useEffect(() => {
    const fromPricing =
      typeof document !== 'undefined' && document.referrer?.includes('/pricing');

    trackFinishSignupView({
      funnelStage: checkoutPlanId ? 'pre_checkout_redirect' : 'plan_selection',
      planId: checkoutPlanId || undefined,
      fromPricing,
      recommendedTier,
    });
  }, [checkoutPlanId, recommendedTier]);

  const waitForAuthToken = async (maxAttempts = 20): Promise<boolean> => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const token = await AuthService.getCurrentUserToken();
      if (token) return true;
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
    return false;
  };

  const checkUserSetup = async () => {
    // Check if user already has complete setup (both academy and subscription)
    // Skip if auth isn't ready yet (e.g., after page refresh)
    try {
      const token = await AuthService.getCurrentUserToken();
      if (!token) {
        // Auth not ready yet, retry after a short delay
        setTimeout(checkUserSetup, 500);
        return;
      }

      // Force refresh subscription (don't use cache - user might have just completed payment)
      // Clear subscription first to ensure we get fresh data
      await invalidateSubscriptionQueries();
      await fetchSubscriptionQuery({ force: true });

      await new Promise((resolve) => setTimeout(resolve, 100));

      const response = await fetchSubscriptionQuery();
      const subscriptionStatus = buildSubscriptionStatusFromResponse(response);
      const hasSubscription = subscriptionStatus.hasActiveSubscription;

      // Only redirect if we have a valid subscription with active/trialing status
      // Don't redirect if subscription is null or undefined (user doesn't have one)
      if (hasSubscription && subscriptionStatus.subscription) {
        navigate('/dashboard');
      }
    } catch {
      // Continue to show plan selection if we can't verify subscription
    }
  };

  // Helper function to get selected price (defined early so it can be used in fetchPlans)
  const getSelectedPrice = (plan: Plan, interval: 'month' | 'year', currencyCode: Currency) => {
    if (!plan.prices || plan.prices.length === 0) return null;

    return plan.prices.find(p =>
      p.recurring?.interval === interval &&
      p.currency?.toUpperCase() === currencyCode.toUpperCase() &&
      p.type === 'recurring'
    ) || null;
  };

  const redirectToSubscriptionCheckout = async (
    academyPlan: Plan | undefined,
    clubPlan: Plan | undefined,
    interval: 'month' | 'year',
    currencyCode: Currency,
    checkoutRegion: Region,
    organizationId?: string
  ) => {
    const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = buildFinishSignupCancelUrl();

    const academyPrice = academyPlan
      ? getSelectedPrice(academyPlan, interval, currencyCode)
      : null;
    const clubPrice = clubPlan ? getSelectedPrice(clubPlan, interval, currencyCode) : null;

    const subscriptionType =
      academyPlan && clubPlan ? 'combined' : clubPlan ? 'club' : 'academy';
    const pricingSelection = readPricingSelection();

    trackCheckoutStarted({
      subscriptionType,
      academyTier: academyPlan
        ? resolveSubscriptionTierForDisplay(academyPlan.name, academyPlan.internalId ?? null)
        : undefined,
      clubTier: clubPlan
        ? normalizeClubPlanTier(clubPlan.internalId ?? null) ?? undefined
        : undefined,
      billingInterval: interval,
      source: pricingSelection?.source ?? undefined,
    });

    const checkoutUrl = await subscriptionService.createSubscriptionCheckoutSession({
      planId: academyPlan?.id ?? clubPlan?.id,
      priceId: academyPrice?.id ?? clubPrice?.id,
      academyPlanId: academyPlan?.id,
      clubPlanId: clubPlan?.id,
      organizationId: organizationId ?? checkoutOrganizationId,
      region: checkoutRegion,
      billingInterval: interval,
      currency: currencyCode,
      successUrl,
      cancelUrl,
      colorScheme: isDark ? 'dark' : 'light',
    });
    clearPricingSelection();
    window.location.href = checkoutUrl;
  };

  const fetchPlans = async () => {
    let keepCheckoutLoadingUntilNavigate = false;
    try {
      setIsLoading(true);

      // Wait for region detection to complete before fetching plans
      // This ensures we get the correct plans for the user's region
      const detectedRegion = await regionService.detectRegion();
      const regionCurrency = getCurrencyFromRegion(detectedRegion);
      setCurrency(regionCurrency);

      // Fetch plans with strict product-line filtering
      let availablePlans = await fetchPlansQuery(detectedRegion, { productLine: 'academy' });
      let availableClubPlans: Plan[] = [];
      if (orgOperationType !== 'academy') {
        availableClubPlans = await subscriptionService.getClubPlans(detectedRegion);
      }

      if (availablePlans.length === 0 || (orgOperationType !== 'academy' && availableClubPlans.length === 0)) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (orgOperationType !== 'club') {
          availablePlans = await fetchPlansQuery(detectedRegion, { productLine: 'academy' });
        }
        if (orgOperationType !== 'academy') {
          availableClubPlans = await subscriptionService.getClubPlans(detectedRegion);
        }
      }

      const needsAcademyPlans = orgOperationType !== 'club';
      const needsClubPlans = orgOperationType !== 'academy';
      if (
        (needsAcademyPlans && availablePlans.length === 0) ||
        (needsClubPlans && availableClubPlans.length === 0)
      ) {
        console.error('[FinishSignup] Plans failed to load');
        toast.error('Failed to load subscription plans. Please refresh the page.');
        setIsLoading(false);
        return;
      }

      const hasAuth = await waitForAuthToken();

      let resolvedCheckoutOrgId = checkoutOrganizationId;
      let firstAcademy: Academy | undefined;

      if (hasAuth) {
        try {
          const academiesRes = await academyService.getUserAcademies();
          firstAcademy = academiesRes.data?.[0];
          if (firstAcademy?.operatingProfile) {
            setAcademyOperatingProfile(firstAcademy.operatingProfile);
          }
          if (firstAcademy?.organizationId) {
            resolvedCheckoutOrgId = resolveCheckoutOrganizationId(onboardingDraft, {
              organizationIdFromState,
              academyOrganizationId: firstAcademy.organizationId,
            });
            setCheckoutOrganizationId(resolvedCheckoutOrgId);
          }
          const academyCountryCode = firstAcademy?.country?.trim().toUpperCase();
          if (academyCountryCode) {
            setShowAdaptivePricing(isAdaptivePricingCountry(academyCountryCode));
          }
        } catch {
          // User may not have academy yet (e.g. skipped setup)
        }
      }

      const sortedPlans = [...availablePlans].sort((a, b) => {
        const priceA = getPlanPrice(a);
        const priceB = getPlanPrice(b);
        return priceA - priceB;
      });

      const sortedClubPlans = [...availableClubPlans].sort((a, b) => {
        const priceA = getPlanPrice(a);
        const priceB = getPlanPrice(b);
        return priceA - priceB;
      });

      setPlans(sortedPlans);
      setClubPlans(sortedClubPlans);

      const profileForRecommendation = deriveOperatingProfileFlags(
        firstAcademy?.operatingProfile ?? onboardingDraft.operatingProfile ?? {},
      );
      const onboardingPlanRecommendation = buildPlanRecommendation(profileForRecommendation);

      if (
        shouldAutoCheckoutFromOnboarding({
          returnedFromCheckoutCancel,
          orgSetupCompleted,
        })
      ) {
        const checkoutTiers = resolveOnboardingCheckoutTiers(
          orgOperationType,
          onboardingDraft,
          onboardingPlanRecommendation,
          {
            selectedAcademyTierFromState: selectedPlanTierFromState,
            selectedClubTierFromState: selectedClubPlanTierFromState,
          },
        );

        const academyPlanForCheckout = checkoutTiers?.academyTier
          ? findPlanByTier(sortedPlans, checkoutTiers.academyTier)
          : undefined;
        const clubPlanForCheckout = checkoutTiers?.clubTier
          ? findClubPlanByTier(sortedClubPlans, checkoutTiers.clubTier)
          : undefined;

        const checkoutReady =
          (orgOperationType === 'academy' && academyPlanForCheckout) ||
          (orgOperationType === 'club' && clubPlanForCheckout) ||
          (orgOperationType === 'both' && academyPlanForCheckout && clubPlanForCheckout);

        if (checkoutReady) {
          if (autoCheckoutStartedRef.current) {
            return;
          }
          autoCheckoutStartedRef.current = true;
          try {
            await redirectToSubscriptionCheckout(
              academyPlanForCheckout,
              clubPlanForCheckout,
              billingInterval,
              regionCurrency,
              detectedRegion,
              resolvedCheckoutOrgId,
            );
            keepCheckoutLoadingUntilNavigate = true;
          } catch (checkoutError: unknown) {
            autoCheckoutStartedRef.current = false;
            console.error('[FinishSignup] Onboarding checkout redirect failed:', checkoutError);
            toast.error(
              checkoutError instanceof Error
                ? checkoutError.message
                : 'Failed to start checkout. Please try again.',
            );
          }
          return;
        }
      }

      // If planId is provided (from URL or sessionStorage), find and redirect to payment
      if (checkoutPlanId && orgOperationType !== 'both') {
        const plansForLine = orgOperationType === 'club' ? sortedClubPlans : sortedPlans;
        const preselectedPlan = findPlanByPreselectionParam(
          plansForLine,
          checkoutPlanId,
          orgOperationType === 'club' ? 'club' : 'academy',
        );

        if (!preselectedPlan) {
          console.warn('[FinishSignup] Plan not found:', {
            requestedPlanId: checkoutPlanId,
            orgOperationType,
            availablePlanIds: plansForLine.map((p) => p.id),
            planNames: plansForLine.map((p) => p.name),
            internalIds: plansForLine.map((p) => p.internalId),
          });
          if (planIdFromStorage) {
            sessionStorage.removeItem('selectedAcademyPlanId');
            sessionStorage.removeItem('selectedClubPlanId');
            sessionStorage.removeItem('selectedPlanId');
          }
        } else {
          if (planIdFromStorage) {
            sessionStorage.removeItem('selectedAcademyPlanId');
            sessionStorage.removeItem('selectedClubPlanId');
            sessionStorage.removeItem('selectedPlanId');
          }
          if (billingIntervalFromStorage) {
            clearStoredBillingInterval();
          }

          const selectedPrice = getSelectedPrice(preselectedPlan, billingInterval, regionCurrency);
          if (!selectedPrice) {
            console.warn('[FinishSignup] No price found for plan:', {
              planId: preselectedPlan.id,
              planName: preselectedPlan.name,
              billingInterval,
              currency: regionCurrency,
            });
          } else {
            if (autoCheckoutStartedRef.current) {
              return;
            }
            autoCheckoutStartedRef.current = true;
            try {
              const { academyPlan, clubPlan } =
                orgOperationType === 'club'
                  ? { academyPlan: undefined, clubPlan: preselectedPlan }
                  : { academyPlan: preselectedPlan, clubPlan: undefined };
              await redirectToSubscriptionCheckout(
                academyPlan,
                clubPlan,
                billingInterval,
                regionCurrency,
                detectedRegion,
                resolvedCheckoutOrgId
              );
              keepCheckoutLoadingUntilNavigate = true;
            } catch (checkoutError: any) {
              autoCheckoutStartedRef.current = false;
              console.error('[FinishSignup] Checkout redirect failed:', checkoutError);
              toast.error(checkoutError?.message || 'Failed to start checkout. Please try again.');
            }
            return;
          }
        }
      }

      // No planId provided - show plan selection
    } catch (error: any) {
      console.error('[FinishSignup] Error fetching plans:', error);
      toast.error(`Failed to load subscription plans: ${error.message || 'Unknown error'}. Please refresh the page.`);
    } finally {
      if (!keepCheckoutLoadingUntilNavigate) {
        setIsLoading(false);
      }
    }
  };

  const starterLimitations = getStarterLimitationSummary(planRecommendation);

  const [selectedAcademyPlan, setSelectedAcademyPlan] = useState<Plan | null>(null);
  const [selectedClubPlan, setSelectedClubPlan] = useState<Plan | null>(null);

  useEffect(() => {
    if (plans.length === 0 && clubPlans.length === 0) return;

    if (orgOperationType !== 'club' && !selectedAcademyPlan) {
      const preselectedAcademy =
        findPlanByTier(plans, activeSelectedPlanTier) ?? findPlanByTier(plans, recommendedTier);
      if (preselectedAcademy) setSelectedAcademyPlan(preselectedAcademy);
    }

    if (orgOperationType !== 'academy' && !selectedClubPlan) {
      const bundledClubTier =
        orgOperationType === 'both'
          ? (activeSelectedClubPlanTier ??
            CLUB_TIER_FOR_ACADEMY_BUNDLE[activeSelectedPlanTier] ??
            recommendedClubTier)
          : activeSelectedClubPlanTier;
      const preselectedClub =
        findClubPlanByTier(clubPlans, bundledClubTier) ??
        findClubPlanByTier(clubPlans, recommendedClubTier);
      if (preselectedClub) setSelectedClubPlan(preselectedClub);
    }
  }, [
    activeSelectedClubPlanTier,
    activeSelectedPlanTier,
    clubPlans,
    orgOperationType,
    plans,
    recommendedClubTier,
    recommendedTier,
    selectedAcademyPlan,
    selectedClubPlan,
  ]);

  const proceedToCheckout = async (academyPlan?: Plan, clubPlan?: Plan) => {
    const checkoutAcademyPlan = academyPlan ?? selectedAcademyPlan ?? undefined;
    const checkoutClubPlan = clubPlan ?? selectedClubPlan ?? undefined;

    if (!checkoutAcademyPlan && !checkoutClubPlan) {
      toast.error('Please select a plan');
      return;
    }

    setSubscribingPlanId(checkoutAcademyPlan?.id ?? checkoutClubPlan?.id ?? null);

    const checkoutBillingInterval = readStoredBillingInterval() ?? billingInterval;

    const fromPricing =
      typeof document !== 'undefined' && document.referrer?.includes('/pricing');
    if (checkoutAcademyPlan) {
      trackPlanSelected(checkoutAcademyPlan.id, checkoutAcademyPlan.name, fromPricing);
    }

    const academyPrice = checkoutAcademyPlan
      ? getSelectedPrice(checkoutAcademyPlan, checkoutBillingInterval, currency)
      : null;
    const clubPrice = checkoutClubPlan
      ? getSelectedPrice(checkoutClubPlan, checkoutBillingInterval, currency)
      : null;

    if (checkoutAcademyPlan && !academyPrice) {
      toast.error('Selected academy pricing option is not available');
      setSubscribingPlanId(null);
      return;
    }
    if (checkoutClubPlan && !clubPrice) {
      toast.error('Selected club pricing option is not available');
      setSubscribingPlanId(null);
      return;
    }

    try {
      const checkoutRegion = regionService.getRegion() || (await regionService.detectRegion());
      await redirectToSubscriptionCheckout(
        checkoutAcademyPlan,
        checkoutClubPlan,
        checkoutBillingInterval,
        currency,
        checkoutRegion,
        checkoutOrganizationId
      );
      clearOnboardingDraft();
    } catch (error: unknown) {
      console.error('[FinishSignup] Error starting checkout:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to start checkout. Please try again.';
      toast.error(message);
      setSubscribingPlanId(null);
    }
  };

  const getPlanPrice = (plan: Plan, interval: 'month' | 'year' = billingInterval): number => {
    const resolved = resolveSubscriptionPlanPrice(plan, {
      currency,
      interval,
    });
    return resolved?.amountMajor ?? 0;
  };

  const handleSelectPlanTier = (
    tier: SubscriptionTier,
    options?: { starterAcknowledged?: boolean },
  ) => {
    if (options?.starterAcknowledged) {
      trackStarterLimitationsAcknowledged(planRecommendation.reasonCodes);
    }
    setActiveSelectedPlanTier(tier);
    const plan = findPlanByTier(plans, tier);
    if (plan) {
      setSelectedAcademyPlan(plan);
    }
  };

  const handleSelectClubPlanTier = (tier: ClubPlanTier) => {
    setActiveSelectedClubPlanTier(tier);
    const plan = findClubPlanByTier(clubPlans, tier);
    if (plan) {
      setSelectedClubPlan(plan);
    }
  };

  const handleAcademyCheckout = async () => {
    const academyPlan =
      selectedAcademyPlan ?? findPlanByTier(plans, activeSelectedPlanTier);
    await proceedToCheckout(academyPlan ?? undefined);
  };

  const handleClubCheckout = async () => {
    const clubPlan =
      selectedClubPlan ?? findClubPlanByTier(clubPlans, activeSelectedClubPlanTier);
    await proceedToCheckout(undefined, clubPlan ?? undefined);
  };

  const handleCombinedCheckout = async () => {
    const academyPlan = selectedAcademyPlan ?? findPlanByTier(plans, activeSelectedPlanTier);
    const clubPlan = selectedClubPlan ?? findClubPlanByTier(clubPlans, activeSelectedClubPlanTier);
    await proceedToCheckout(academyPlan ?? undefined, clubPlan ?? undefined);
  };

  const handleSelectBundle = (academyTier: SubscriptionTier, clubTier: ClubPlanTier) => {
    setActiveSelectedPlanTier(academyTier);
    setActiveSelectedClubPlanTier(clubTier);

    const clubPlan = findClubPlanByTier(clubPlans, clubTier);
    if (clubPlan) {
      setSelectedClubPlan(clubPlan);
    }

    const academyPlan = findPlanByTier(plans, academyTier);
    if (!academyPlan) return;

    const needsStarterAck =
      academyTier === 'starter' &&
      recommendedTier !== 'starter' &&
      starterLimitations.length > 0 &&
      !onboardingDraft.starterLimitationsAcknowledged;

    if (needsStarterAck) {
      setPendingStarterPlan(academyPlan);
      setStarterModalOpen(true);
      return;
    }

    setSelectedAcademyPlan(academyPlan);
  };

  const handleStarterConfirm = async () => {
    trackStarterLimitationsAcknowledged(planRecommendation.reasonCodes);
    setStarterModalOpen(false);
    if (pendingStarterPlan) {
      if (orgOperationType === 'both') {
        setSelectedAcademyPlan(pendingStarterPlan);
        setActiveSelectedPlanTier('starter');
      } else {
        await proceedToCheckout(pendingStarterPlan);
      }
      setPendingStarterPlan(null);
    }
  };

  const renderCheckoutActions = (
    onCheckout: () => void | Promise<void>,
    disabled: boolean,
  ) => (
    <div className="space-y-4">
      <Button
        type="button"
        size="lg"
        className="w-full"
        disabled={disabled}
        onClick={() => void onCheckout()}
      >
        {subscribingPlanId ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing…
          </>
        ) : (
          'Start free trial'
        )}
      </Button>
      <div className="space-y-2">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <CheckCircle className="mr-1 inline h-4 w-4 text-green-500" />
          No charge during the 14-day free trial
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <CheckCircle className="mr-1 inline h-4 w-4 text-green-500" />
          Cancel anytime—no questions asked
        </p>
      </div>
      {plans.length > 0 ? (
        <button
          type="button"
          className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline dark:text-primary-400 dark:hover:text-primary-300"
          onClick={() => setFeaturesModalOpen(true)}
        >
          View all features
        </button>
      ) : null}
    </div>
  );

  const finishIntro =
    orgSetupCompleted
      ? orgOperationType === 'club'
        ? `Great! Your club is set up. We recommend ${formatClubPlanTierLabel(recommendedClubTier)} based on your answers.`
        : orgOperationType === 'both'
          ? `Great! Your organization is set up. We recommend ${formatPlanTierLabel(recommendedTier)} for academy and ${formatClubPlanTierLabel(recommendedClubTier)} for club.`
          : `Great! Your academy is set up. We recommend the ${formatPlanTierLabel(recommendedTier)} plan based on your answers.`
      : isSocialLogin
        ? `Welcome! We recommend the ${formatPlanTierLabel(recommendedTier)} plan based on your setup answers.`
        : userEmail
          ? `Your email has been verified. We recommend the ${formatPlanTierLabel(recommendedTier)} plan to get started.`
          : `Choose a subscription plan to get started with CourtSync. We recommend ${formatPlanTierLabel(recommendedTier)}.`;

  const finishShellLeft = (
    <>
      <PlatformCheckoutBrand className="mb-6" />
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-3">
        Choose your plan
      </h1>
      <p className="text-gray-600 dark:text-gray-400 mb-5 text-sm sm:text-base">
        {finishIntro}
      </p>

      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50/80 dark:bg-green-900/20 px-4 py-3 mb-6">
        <div className="flex items-start gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-green-900 dark:text-green-200 text-sm">14-day free trial on every plan</p>
            <p className="text-xs text-green-800 dark:text-green-300 mt-1">
              You won&apos;t be charged until the trial ends. Cancel anytime.
            </p>
          </div>
        </div>
      </div>

      {userEmail && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Signed in as <span className="font-medium text-gray-900 dark:text-white">{userEmail}</span>
        </p>
      )}

      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
          What happens next
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select monthly or yearly billing, pick a plan, then complete checkout on Stripe. Your subscription starts after
          you finish payment.
        </p>
      </div>
    </>
  );

  if (isLoading) {
    return (
      <PlatformCheckoutShell
        left={
          <>
            <PlatformCheckoutBrand className="mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Subscribe</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              All plans include a 14-day free trial. You&apos;ll finish setup on Stripe&apos;s secure checkout.
            </p>
          </>
        }
      >
        <div className="flex flex-col items-center justify-center min-h-[220px] py-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mb-4" />
          <p className="text-lg font-medium text-gray-900 dark:text-white">
            {checkoutPlanId || orgSetupCompleted
              ? 'Taking you to secure checkout…'
              : 'Loading subscription plans…'}
          </p>
        </div>
      </PlatformCheckoutShell>
    );
  }

  return (
    <PlatformCheckoutShell left={finishShellLeft}>
      <div className="w-full">
        <p className="mb-4 text-center text-sm text-muted-foreground lg:text-left">
          Select billing, then choose a plan. All plans include a 14-day free trial.
        </p>

        {showAdaptivePricing ? (
          <AdaptivePricingNote className="mb-6 text-center lg:text-left" />
        ) : null}

        {orgOperationType === 'both' ? (
          <div className="space-y-6">
            <CombinedOnboardingPlanSelection
              academyRecommendation={planRecommendation}
              clubRecommendation={clubPlanRecommendation}
              selectedAcademyTier={
                selectedAcademyPlan
                  ? (resolveSubscriptionTierForDisplay(
                      selectedAcademyPlan.name,
                      selectedAcademyPlan.internalId ?? null,
                    ) as SubscriptionTier)
                  : activeSelectedPlanTier
              }
              selectedClubTier={
                selectedClubPlan
                  ? (normalizeClubPlanTier(selectedClubPlan.internalId ?? null) ?? activeSelectedClubPlanTier)
                  : activeSelectedClubPlanTier
              }
              onSelectBundle={handleSelectBundle}
              marketCountry={onboardingDraft.country}
            />
            {renderCheckoutActions(
              handleCombinedCheckout,
              Boolean(subscribingPlanId) || !selectedAcademyPlan || !selectedClubPlan,
            )}
          </div>
        ) : orgOperationType === 'club' ? (
          <div className="space-y-6">
            <ClubOnboardingPlanSelection
              recommendation={clubPlanRecommendation}
              selectedClubPlanTier={activeSelectedClubPlanTier}
              onSelectClubPlanTier={handleSelectClubPlanTier}
              marketCountry={onboardingDraft.country}
            />
            {renderCheckoutActions(handleClubCheckout, Boolean(subscribingPlanId) || !selectedClubPlan)}
          </div>
        ) : (
          <div className="space-y-6">
            <OnboardingPlanSelection
              recommendation={planRecommendation}
              selectedPlanTier={activeSelectedPlanTier}
              onSelectPlanTier={handleSelectPlanTier}
              marketCountry={onboardingDraft.country}
            />
            {renderCheckoutActions(
              handleAcademyCheckout,
              Boolean(subscribingPlanId) || !selectedAcademyPlan,
            )}
          </div>
        )}

        <Dialog open={featuresModalOpen} onOpenChange={setFeaturesModalOpen}>
          <DialogContent className="flex h-[min(92vh,52rem)] w-[min(96vw,56rem)] max-w-none flex-col gap-0 overflow-hidden p-0">
            <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
              <DialogTitle>Full feature list by plan</DialogTitle>
              <DialogDescription>
                Detailed capabilities for each tier. Highlights above are shortened for easier scanning.
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
              <FinishSignupFeatureComparison plans={plans} />
            </div>
          </DialogContent>
        </Dialog>

        <StarterLimitationsModal
          open={starterModalOpen}
          onOpenChange={setStarterModalOpen}
          limitations={starterLimitations}
          onConfirm={() => void handleStarterConfirm()}
        />
      </div>
    </PlatformCheckoutShell>
  );
}
